import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Minus, Plus, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { fetchProduct } from "@/lib/shopify";
import { WEAR_PRODUCTS, BIG_LIVING_PRODUCTS } from "@/lib/placeholderData";
import SizeGuideModal from "@/components/SizeGuideModal";
import { TEST_IDS } from "@/constants/testIds";

const ALL_PLACEHOLDERS = [...WEAR_PRODUCTS, ...BIG_LIVING_PRODUCTS];

function parseSizes(variants) {
  if (!variants || variants.length === 0) return [];
  const sizes = [];
  const seen = new Set();
  for (const v of variants) {
    const opts = v.selectedOptions || [];
    const sizeOpt = opts.find((o) => o.name.toLowerCase() === "size");
    if (sizeOpt && !seen.has(sizeOpt.value)) {
      seen.add(sizeOpt.value);
      sizes.push({
        label: sizeOpt.value,
        variantId: v.id,
        available: v.availableForSale !== false,
      });
    }
  }
  return sizes;
}

function parseFit(tags) {
  const t = (tags || []).map((x) => x.toLowerCase());
  if (t.includes("generous") || t.includes("generous fit") || t.includes("runs generous") || t.includes("runs-generous")) return "Runs Generous";
  if (t.includes("runs-small") || t.includes("runs small")) return "Runs Small";
  return "Fits True to Size";
}

export default function ProductDetailPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [sizes, setSizes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setSelectedImage(0);
      setSelectedSize("");

      const shopifyProduct = await fetchProduct(handle);
      if (!cancelled && shopifyProduct) {
        const images = shopifyProduct.images || [];
        const variants = shopifyProduct.variants || [];
        const parsedSizes = parseSizes(variants);
        const fit = parseFit(shopifyProduct.tags);

        setSizes(parsedSizes);
        if (parsedSizes.length > 0) setSelectedSize(parsedSizes[0].label);

        setProduct({
          id: shopifyProduct.id,
          title: shopifyProduct.title,
          handle: shopifyProduct.handle,
          description: shopifyProduct.description || shopifyProduct.descriptionHtml,
          category: (shopifyProduct.productType || "").toUpperCase(),
          fit,
          price: parseFloat(shopifyProduct.priceRange?.minVariantPrice?.amount || 0),
          currency: shopifyProduct.priceRange?.minVariantPrice?.currencyCode || "USD",
          images: images.map((img) => img.url || img),
          variants,
          variantId: variants?.[0]?.id || null,
          isShopify: true,
        });
        setLoading(false);
        return;
      }

      const placeholder = ALL_PLACEHOLDERS.find((p) => p.handle === handle);
      if (!cancelled && placeholder) {
        setProduct({
          ...placeholder,
          fit: placeholder.fit || "Fits True to Size",
          description:
            "This item has been carefully selected and tested to ensure the best fit and quality. Every product in our collection is checked for comfort, durability, and joy.",
          images: placeholder.image ? [placeholder.image] : [],
          variants: [],
          isShopify: false,
        });
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-plum/20 border-t-plum rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center gap-4">
        <p className="text-plum-light">Product not found.</p>
        <Button onClick={() => navigate("/")} variant="outline" className="rounded-full border-plum/20 text-plum">
          Back to shop
        </Button>
      </div>
    );
  }

  const selectedVariant = sizes.find((s) => s.label === selectedSize);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addItem({
        id: product.id,
        title: product.title,
        handle: product.handle,
        price: product.price,
        image: product.images?.[0] || null,
        category: product.category,
        color: product.color,
        variantId: selectedVariant?.variantId || product.variantId || null,
        size: selectedSize || null,
      });
    }
  };

  return (
    <div data-testid={TEST_IDS.DETAIL.root} className="min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Back */}
        <motion.button
          data-testid={TEST_IDS.DETAIL.backButton}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-plum-light hover:text-plum mb-8 transition-colors duration-200"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left — Images */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div
              data-testid={TEST_IDS.DETAIL.image}
              className="aspect-[3/4] rounded-3xl overflow-hidden mb-4"
              style={{ backgroundColor: product.color || "#FDEAEB" }}
            >
              {product.images?.[selectedImage] && (
                <img src={product.images[selectedImage]} alt={product.title} className="w-full h-full object-cover" />
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto py-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-16 h-20 rounded-xl overflow-hidden shrink-0 border-2 transition-colors duration-200 ${
                      i === selectedImage ? "border-plum" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Right — Details */}
          <motion.div className="lg:pt-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}>
            {product.category && (
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-plum-light mb-2">
                {product.category}
              </p>
            )}

            <h1 data-testid={TEST_IDS.DETAIL.title} className="font-serif text-3xl lg:text-4xl font-bold text-plum tracking-tight">
              {product.title}
            </h1>

            {/* Price */}
            <p data-testid={TEST_IDS.DETAIL.price} className="mt-4 text-2xl font-serif font-bold text-plum">
              ${product.price.toLocaleString()}
            </p>

            {/* Fit badge — prominent, amber */}
            <div className="mt-3">
              <Badge
                data-testid="product-detail-fit-badge"
                className="bg-amber/15 text-amber border-0 text-sm font-semibold rounded-full px-4 py-1"
              >
                {product.fit}
              </Badge>
            </div>

            {/* Checked for you */}
            <div className="mt-5 flex items-center gap-2 bg-plum/5 rounded-xl px-4 py-3 w-fit">
              <div className="w-6 h-6 rounded-full bg-plum flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-semibold text-plum">Checked for you</span>
            </div>

            {/* Description */}
            <p data-testid={TEST_IDS.DETAIL.description} className="mt-6 text-base text-plum-light leading-relaxed">
              {product.description}
            </p>

            {/* Size selector — button grid */}
            {sizes.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-plum">Size</label>
                  <button
                    data-testid="size-guide-btn"
                    onClick={() => setSizeGuideOpen(true)}
                    className="flex items-center gap-1 text-xs font-medium text-plum-light hover:text-amber transition-colors duration-200"
                  >
                    <Ruler className="w-3.5 h-3.5" />
                    Size guide
                  </button>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {sizes.map((s) => {
                    const isSelected = selectedSize === s.label;
                    const isOos = !s.available;
                    return (
                      <button
                        key={s.label}
                        data-testid={`size-option-${s.label}`}
                        onClick={() => !isOos && setSelectedSize(s.label)}
                        disabled={isOos}
                        className={`
                          relative min-w-[52px] px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors duration-200
                          ${isSelected
                            ? "bg-plum text-white border-plum"
                            : isOos
                              ? "bg-cream text-plum/30 border-plum/10 cursor-not-allowed"
                              : "bg-white text-plum border-plum/15 hover:border-plum/40"
                          }
                        `}
                      >
                        <span className={isOos ? "line-through" : ""}>{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6 flex items-center gap-4">
              <span className="text-sm font-semibold text-plum">Quantity</span>
              <div className="flex items-center gap-3 border border-plum/10 rounded-full px-4 py-2">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-1 text-plum-light hover:text-plum transition-colors" aria-label="Decrease">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-sm font-semibold text-plum w-8 text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-1 text-plum-light hover:text-plum transition-colors" aria-label="Increase">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Add to bag — full width, deep plum */}
            <Button
              data-testid={TEST_IDS.DETAIL.addToCart}
              onClick={handleAddToCart}
              className="w-full mt-8 rounded-full bg-plum text-white hover:bg-plum/90 py-6 font-sans font-semibold text-base transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              Add to bag — ${(product.price * quantity).toLocaleString()}
            </Button>
          </motion.div>
        </div>
      </div>

      <SizeGuideModal open={sizeGuideOpen} onOpenChange={setSizeGuideOpen} />
    </div>
  );
}
